import { useState } from 'react';
import { genreOptions, regionOptions, yearOptions, sortOptions } from '@/mocks/filters';

interface FilterBarProps {
  genre: string;
  region: string;
  year: string;
  sort: string;
  showGenre?: boolean;
  onChange: (key: string, value: string) => void;
  onReset: () => void;
}

interface ChipRowProps {
  label: string;
  options: string[];
  value: string;
  onSelect: (value: string) => void;
}

function ChipRow({ label, options, value, onSelect }: ChipRowProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
      <span className="shrink-0 pt-1 text-sm text-foreground-500 sm:w-12">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = option === value;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onSelect(option)}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                active
                  ? 'bg-primary-500 font-medium text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200 hover:text-foreground-950'
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({
  genre,
  region,
  year,
  sort,
  showGenre = true,
  onChange,
  onReset,
}: FilterBarProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 md:mb-4">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="flex cursor-pointer items-center gap-2 rounded-md bg-background-200/70 px-3 py-2 text-sm font-medium text-foreground-800 md:hidden"
        >
          <i className="ri-filter-3-line"></i>
          筛选条件
          <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`}></i>
        </button>
        <span className="hidden items-center gap-2 text-sm font-medium text-foreground-800 md:flex">
          <i className="ri-filter-3-line text-primary-500"></i>
          筛选与排序
        </span>
        <button
          type="button"
          onClick={onReset}
          className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-500 transition-colors hover:text-primary-500"
        >
          <i className="ri-refresh-line"></i>
          重置
        </button>
      </div>

      <div className={`${expanded ? 'mt-4 flex' : 'hidden'} flex-col gap-4 md:mt-0 md:flex`}>
        {showGenre ? (
          <ChipRow label="类型" options={genreOptions} value={genre} onSelect={(value) => onChange('genre', value)} />
        ) : null}
        <ChipRow label="地区" options={regionOptions} value={region} onSelect={(value) => onChange('region', value)} />
        <ChipRow label="年份" options={yearOptions} value={year} onSelect={(value) => onChange('year', value)} />

        <div className="flex flex-col gap-2 border-t border-background-200/70 pt-4 sm:flex-row sm:items-center sm:gap-4">
          <span className="shrink-0 text-sm text-foreground-500 sm:w-12">排序</span>
          <div className="inline-flex w-fit items-center gap-1 rounded-full bg-background-200/60 px-1 py-1">
            {sortOptions.map((option) => {
              const active = option.key === sort;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => onChange('sort', option.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    active ? 'bg-primary-500 font-medium text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}